import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function IndividualQuiz({ user }) {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [quiz, setQuiz] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [finalScore, setFinalScore] = useState(null);

  useEffect(() => {
    startQuiz();
  }, []);

  useEffect(() => {
    if (quiz && !showResult) {
      const currentQuestion = quiz.questions[currentQuestionIndex];
      setTimeLeft(currentQuestion.time_limit);
      setQuestionStartTime(Date.now());
      setSelectedAnswer(null);
    }
  }, [currentQuestionIndex, quiz]);

  useEffect(() => {
    if (timeLeft > 0 && !showResult && !selectedAnswer) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !selectedAnswer) {
      handleNextQuestion();
    }
  }, [timeLeft, showResult, selectedAnswer]);

  const startQuiz = async () => {
    try {
      const courseworkId = searchParams.get('coursework_id');
      const courseId = searchParams.get('course_id');
      
      const params = {
        user_id: user.id
      };
      
      if (courseworkId && courseId) {
        params.coursework_id = courseworkId;
        params.course_id = courseId;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/quizzes/${quizId}/individual/start`,
        null,
        { params }
      );
      setQuiz(response.data);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Error starting quiz:', error);
      alert('Error al cargar el quiz');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    if (selectedAnswer !== null) return;
    
    const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
    
    setSelectedAnswer(answerIndex);
    // Don't add to answers here, will be added in submitQuiz
  };

  const handleNextQuestion = () => {
    if (selectedAnswer === null) {
      // Time ran out, save as unanswered
      const newAnswers = [...answers, {
        question_index: currentQuestionIndex,
        answer: -1, // -1 indicates no answer
        time_taken: quiz.questions[currentQuestionIndex].time_limit
      }];
      setAnswers(newAnswers);
      
      if (currentQuestionIndex < quiz.questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        submitQuizWithAnswers(newAnswers);
      }
    } else {
      // Save the current answer
      const newAnswers = [...answers, {
        question_index: currentQuestionIndex,
        answer: selectedAnswer,
        time_taken: Math.floor((Date.now() - questionStartTime) / 1000)
      }];
      setAnswers(newAnswers);
      
      if (currentQuestionIndex < quiz.questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        submitQuizWithAnswers(newAnswers);
      }
    }
  };

  const submitQuizWithAnswers = async (finalAnswers) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/quizzes/${quizId}/individual/submit`,
        {
          session_id: sessionId,
          user_id: user.id,
          answers: finalAnswers
        }
      );
      
      setFinalScore(response.data);
      setShowResult(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('Error al enviar las respuestas');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <p className="mt-4 text-white">Cargando quiz...</p>
        </div>
      </div>
    );
  }

  if (showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Score Summary */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 mb-6 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">¡Quiz Completado!</h2>
            <div className="my-6">
              <div className="text-5xl font-bold text-primary mb-2">{finalScore.score}</div>
              <div className="text-gray-600">puntos totales</div>
            </div>
            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Respuestas correctas:</span>
                <span className="font-semibold">{finalScore.correct_answers} / {finalScore.total_questions}</span>
              </div>
              {finalScore.grade_percentage !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Calificación:</span>
                  <span className="font-semibold text-primary">{finalScore.grade_percentage} / 100</span>
                </div>
              )}
            </div>
          </div>

          {/* Questions Review */}
          {finalScore.review && finalScore.review.length > 0 && (
            <div className="space-y-4 mb-6">
              <h3 className="text-2xl font-bold text-white text-center mb-4">📝 Resumen de Respuestas</h3>
              {finalScore.review.map((item, index) => (
                <div 
                  key={index}
                  className={`bg-white rounded-xl shadow-lg p-6 ${
                    item.is_correct ? 'border-l-8 border-green-500' : 'border-l-8 border-red-500'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-lg font-bold text-gray-800 flex-1">
                      Pregunta {item.question_number}: {item.question_text}
                    </h4>
                    <div className="ml-4">
                      {item.is_correct ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                          ✓ Correcto
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                          ✗ Incorrecto
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {item.options.map((option, optIndex) => {
                      const isStudentAnswer = optIndex === item.student_answer;
                      const isCorrectAnswer = optIndex === item.correct_answer;
                      
                      let bgColor = 'bg-gray-50';
                      let textColor = 'text-gray-700';
                      let borderClass = '';
                      
                      if (isCorrectAnswer) {
                        bgColor = 'bg-green-100';
                        textColor = 'text-green-800 font-semibold';
                        borderClass = 'border-2 border-green-500';
                      }
                      
                      if (isStudentAnswer && !isCorrectAnswer) {
                        bgColor = 'bg-red-100';
                        textColor = 'text-red-800 font-semibold';
                        borderClass = 'border-2 border-red-500';
                      }
                      
                      return (
                        <div 
                          key={optIndex}
                          className={`p-3 rounded-lg ${bgColor} ${textColor} ${borderClass}`}
                        >
                          {option}
                        </div>
                      );
                    })}
                  </div>
                  
                  {item.points_earned > 0 && (
                    <div className="mt-3 text-sm text-gray-600">
                      Puntos ganados: <span className="font-bold text-primary">{item.points_earned}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Back Button */}
          <div className="text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-4 bg-white text-primary rounded-xl font-bold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900">
      {/* Header */}
      <div className="bg-white bg-opacity-10 backdrop-blur-md p-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center text-white">
          <div className="text-sm font-medium">
            Pregunta {currentQuestionIndex + 1} de {quiz.questions.length}
          </div>
          <div className="text-2xl font-bold">
            ⏱️ {timeLeft}s
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-4xl mx-auto mt-2">
          <div className="h-2 bg-white bg-opacity-20 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {currentQuestion.question_text}
          </h2>
          {currentQuestion.image_url && (
            <img
              src={currentQuestion.image_url}
              alt="Question"
              className="w-full max-h-64 object-contain rounded-lg mb-4"
            />
          )}
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentQuestion.options.map((option, index) => {
            const colors = [
              { bg: 'bg-red-500', hover: 'hover:bg-red-600', selected: 'ring-red-700' },
              { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', selected: 'ring-blue-700' },
              { bg: 'bg-yellow-500', hover: 'hover:bg-yellow-600', selected: 'ring-yellow-700' },
              { bg: 'bg-green-500', hover: 'hover:bg-green-600', selected: 'ring-green-700' }
            ];
            const color = colors[index];

            return (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={selectedAnswer !== null}
                className={`p-6 rounded-xl text-white font-bold text-lg transition-all ${
                  color.bg
                } ${selectedAnswer === null ? color.hover : ''} ${
                  selectedAnswer === index ? `ring-4 ${color.selected} scale-105` : ''
                } disabled:cursor-not-allowed`}
              >
                {option}
              </button>
            );
          })}
        </div>

        {/* Next button */}
        {selectedAnswer !== null && (
          <div className="mt-6 text-center">
            <button
              onClick={handleNextQuestion}
              className="px-8 py-4 bg-white text-primary rounded-xl font-bold text-xl hover:bg-gray-100 transition shadow-lg"
            >
              {currentQuestionIndex < quiz.questions.length - 1 ? 'Siguiente Pregunta →' : 'Ver Resultados 🎉'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default IndividualQuiz;
